import { MessageMedium } from 'constants/messageMediums';
import { CompleteQuery } from 'modules/queryBuilder/types';
import {
  BehavioralInsightPresentationMode,
  DemographicInsightPresentationMode,
  EventTypes,
} from 'modules/domains/segmentation';

export type AudienceCount = {
  name: string;
  count: number;
};

export type HistogramBucket = {
  time: number;
  value: number;
};

export type BehaviorMetric = {
  value: number;
  histogram?: { bucket: HistogramBucket };
};

export type RawBehaviorQuery = {
  'Total Purchases'?: BehaviorMetric | null;
  'Total Delivered'?: BehaviorMetric | null;
  'Total Unsubscribes'?: BehaviorMetric | null;
  'Unsubscribe Rate'?: BehaviorMetric | null;
  'Unique Conversions / Unique Sends'?: BehaviorMetric | null;
  'Unique Purchases'?: BehaviorMetric | null;
  Revenue?: BehaviorMetric | null;
  'Revenue / Purchase'?: BehaviorMetric | null;
};

export type BehaviorQuery = {
  totalPurchases?: BehaviorMetric;
  totalSent?: BehaviorMetric;
  totalUnsubscribes?: BehaviorMetric;
  conversionRate?: BehaviorMetric;
  unsubscribeRate?: BehaviorMetric;
  uniquePurchases?: BehaviorMetric;
  revenue?: BehaviorMetric;
  avgOrderValue?: BehaviorMetric;
};

export const BehaviorQueryKeysMap: Record<keyof RawBehaviorQuery, keyof BehaviorQuery> = {
  'Total Purchases': 'totalPurchases',
  'Total Delivered': 'totalSent',
  'Total Unsubscribes': 'totalUnsubscribes',
  'Unsubscribe Rate': 'unsubscribeRate',
  'Unique Conversions / Unique Sends': 'conversionRate',
  'Unique Purchases': 'uniquePurchases',
  Revenue: 'revenue',
  'Revenue / Purchase': 'avgOrderValue',
};

export type ProfileQuery = {
  brandAffinityLevels?: AudienceCount[] | null;
  devices?: AudienceCount[] | null;
  emailDomains?: AudienceCount[] | null;
  locations?: AudienceCount[] | null;
  locales?: AudienceCount[] | null;
};

export type InsightQueryRequest = {
  emailListId?: number;
  searchQuery?: CompleteQuery; // only required for Dynamic Lists
  startDate?: string | null;
  endDate?: string | null;
};

export type InsightQueryResponse = {
  behavior: RawBehaviorQuery;
  profile: ProfileQuery;
};

export type CustomBehavioralInsight = {
  event: string;
  eventName: string;
  mode: BehavioralInsightPresentationMode;
  dataType: EventTypes;
  behavior?: BehaviorMetric;
};

export type BehavioralEvent = {
  event: string;
  dataType: EventTypes;
};

export type ConversionCampaign = {
  id: number;
  templateId: number;
  campaignState: string;
  createdAt: number;
  messageMedium: MessageMedium;
  name: string;
};

export type HighestConversionCampaign = {
  campaign: ConversionCampaign;
  conversionRate: number;
};

export type ConversionResponse = {
  highestConvertingCampaigns: HighestConversionCampaign[];
  totalCampaigns: number;
  overallConversionRate: number;
};

export type ConversionRequest = InsightQueryRequest & {
  page?: number;
  pageSize?: number;
  eventName: string | null;
};

export enum InsightType {
  behavioral = 'behavioral',
  demographics = 'demographics',
  listUsage = 'listUsage',
}

export type CustomDemographicInsight = {
  contactProperty: string;
  mode: DemographicInsightPresentationMode;
  values?: AudienceCount[];
};
